import React, { useEffect, useRef } from "react";
import { MOBILE_INPUT_FONT_SIZE_STYLE } from "../../utils/mobileInputStyles";

/** 16px — prevents iOS Safari auto-zoom on input focus. */
export const MODAL_INPUT_CLASS =
  "w-full rounded-xl border border-slate-200 px-4 py-2.5 text-[16px] text-slate-900 placeholder:text-slate-400 focus:border-[#38513b] focus:outline-none focus:ring-1 focus:ring-[#38513b]";

export const MODAL_INPUT_STYLE = MOBILE_INPUT_FONT_SIZE_STYLE;

export const MODAL_TEXTAREA_CLASS =
  "w-full min-h-[100px] rounded-xl border border-slate-200 px-4 py-3 text-[16px] text-slate-900 placeholder:text-slate-400 focus:border-[#38513b] focus:outline-none focus:ring-1 focus:ring-[#38513b]";

export function isMobileViewport() {
  if (typeof window === "undefined") return false;
  return window.matchMedia("(max-width: 767px)").matches;
}

/**
 * Full-screen modal tuned for mobile: bottom-aligned sheet, 16px inputs, no autofocus zoom.
 * Add `va-modal` on backdrop for global CSS safeguards in App.css.
 */
/** Default panel width when `panelClassName` does not include a `max-w-*` utility. */
const DEFAULT_PANEL_MAX_WIDTH = "max-w-md";

export default function MobileFriendlyModal({
  children,
  onClose,
  panelClassName = "",
  backdropClassName = "",
  zClass = "z-[100000]",
}) {
  const panelRef = useRef(null);
  const hasCustomMaxWidth = /\bmax-w-/.test(panelClassName);
  const panelMaxWidthClass = hasCustomMaxWidth ? "" : DEFAULT_PANEL_MAX_WIDTH;

  useEffect(() => {
    if (!isMobileViewport()) return undefined;
    const id = requestAnimationFrame(() => {
      panelRef.current?.scrollIntoView({ block: "nearest", behavior: "auto" });
    });
    return () => cancelAnimationFrame(id);
  }, []);

  return (
    <div
      className={`va-modal fixed inset-0 ${zClass} flex items-end justify-center overflow-y-auto overscroll-contain bg-slate-900/50 px-4 pb-[max(1rem,env(safe-area-inset-bottom))] pt-4 sm:items-center sm:py-8 ${backdropClassName}`}
      role="dialog"
      aria-modal="true"
      onClick={(e) => {
        if (onClose && e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={panelRef}
        className={`va-modal-panel mx-auto my-auto w-full ${panelMaxWidthClass} max-h-[min(90dvh,calc(100dvh-2rem))] overflow-y-auto overscroll-contain rounded-2xl border border-slate-200 bg-white p-6 shadow-xl sm:max-h-[85vh] ${panelClassName}`}
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>
  );
}
